/* global React, Modal, Button, Checkbox, Badge, Icon, InvoicePayAccountSelect, AddBankAccountDialog, t */
const receiptUploadFileKey = (file, index) => [
  index,
  file?.name || 'bilag',
  file?.size || 0,
  file?.lastModified || 0,
].join(':');

const receiptUploadDecisionLabel = (decision) => {
  if (decision?.kind === 'account') return t('inbox.uploadPaymentAssigned', { account: decision.accountCode });
  if (decision?.kind === 'unpaid') return t('inbox.uploadPaymentUnpaid');
  if (decision?.kind === 'analyze') return t('inbox.uploadPaymentAnalyze');
  return t('inbox.uploadPaymentUnassigned');
};

// Hvilken bankkonto knappen peker paa.
//
// Med én konto er «Bank» hele sannheten, og et kontonavn under ville bare vaert
// stoy. Har foretaket flere, maa knappen si hvilken — men den kan ikke si det
// med navnet: kontoen heter det brukeren har kalt den, og «Driftskonto Sparebank
// 1 Ostlandet» sprenger en knapp som deler linja med en annen.
//
// De fire siste sifrene i kontonummeret er den ene opplysningen som alltid er
// like kort og alltid gjenkjennelig — det er slik nettbankene selv skiller
// kontoer. Mangler nummeret, faller vi tilbake paa navnet, og CSS-en klipper det
// om det blir langt.
const receiptUploadAccountHint = (account, several) => {
  if (!several) return '';
  const digits = String(account?.accountNumber || account?.number || '').replace(/\D/g, '');
  return digits.length >= 4 ? digits.slice(-4) : String(account?.name || '').trim();
};

const receiptUploadPrimaryOperatingAccount = () => {
  const state = window.PlummeBackend?.appState || {};
  const settings = state.settings || {};
  const accounts = (Array.isArray(settings.bankAccounts) ? settings.bankAccounts : []).filter((account) => {
    const kind = String(account?.kind || 'operating').toLowerCase();
    const code = String(account?.ledgerAccountCode || account?.ledger || account?.accountCode || '').trim();
    const number = String(account?.accountNumber || account?.number || account?.iban || '').replace(/\s/g, '');
    return account?.active !== false && kind !== 'private' && kind !== 'savings' && code && number;
  });
  const visible = accounts.filter((account) => {
    const kind = String(account.kind || 'operating').toLowerCase();
    if (kind === 'private' || kind === 'savings') return false;
    return typeof account.showInPaymentChoices === 'boolean' ? account.showInPaymentChoices : (account.invoiceDefault || accounts.length === 1);
  });
  const configuredDefault = accounts.find((entry) => String(entry.ledgerAccountCode || entry.ledger || entry.accountCode || '') === String(settings.defaultReceiptPaymentAccountCode || ''));
  const account = configuredDefault || visible.find((entry) => entry.invoiceDefault) || visible[0] || accounts.find((entry) => String(entry.kind || 'operating').toLowerCase() === 'operating') || null;
  const code = String(account?.ledgerAccountCode || account?.ledger || account?.accountCode || '').trim();
  return { code, account, hint: receiptUploadAccountHint(account, visible.length > 1) };
};

const receiptUploadPrivateAccountCode = () => {
  const state = window.PlummeBackend?.appState || {};
  const settlement = window.PlummeReceiptPaymentSource?.privateSettlementForState?.(state);
  return String(settlement?.accountCode || '').trim();
};

// Valget for ett bilag.
//
// Den som holder en kvittering i haanden — eller nettopp har plukket én fil —
// har allerede bestemt seg. Nesten alt er betalt fra bankkontoen eller privat,
// og resten kan Superplumma lese ut av bilaget selv. Valget er derfor hele
// handlingen: kilden velges, og bilaget gaar sin vei. Ingen bekreftelse etterpaa
// — «Aapne kamera» og «Last opp 1» var et trykk til for et valg som allerede var
// tatt.
//
// De to vanlige staar ved siden av hverandre, Superplumma paa egen linje, og
// enhver annen konto ligger i nedtrekket under. Ogsaa der er valget endelig.
//
// Delt av alle tre stedene spoersmaalet stilles — kameraet, innboksens
// enkeltopplasting og fordelingen av flere bilag — slik at de ikke glir fra
// hverandre. Forskjellen er bare hva et valg gjor: aapner kameraet, laster opp
// bilaget, eller setter kilden paa de radene brukeren har merket.
function ReceiptPaymentSourceChoices({ disabled = false, onPick, children }) {
  const [bankSetupOpen, setBankSetupOpen] = React.useState(false);
  const operating = receiptUploadPrimaryOperatingAccount();
  const privateCode = receiptUploadPrivateAccountCode();
  const bankMissing = !operating.code;
  const pickAccount = (accountCode) => { if (accountCode) onPick({ kind: 'account', accountCode }); };
  return <div className="camera-payment" data-help={t('helpTopics.uploadPayment.body')} data-help-title={t('helpTopics.uploadPayment.title')}>
    <div className="camera-payment-row">
      <button type="button" className="camera-payment-choice" disabled={disabled}
        onClick={() => bankMissing ? setBankSetupOpen(true) : pickAccount(operating.code)} data-help={t('helpTopics.uploadPayment.body')} data-help-title={t('helpTopics.uploadPayment.title')}>
        <Icon name="bank" size={20}/>
        <span><b>{t('inbox.uploadPaymentBank')}</b>{bankMissing ? <small>{t('inbox.uploadPaymentBankSetup')}</small> : operating.hint && <small>{operating.hint}</small>}</span>
      </button>
      <button type="button" className={'camera-payment-choice' + (bankMissing ? ' is-recommended' : '')} disabled={disabled || !privateCode}
        onClick={() => pickAccount(privateCode)} data-help={t('helpTopics.uploadPayment.body')} data-help-title={t('helpTopics.uploadPayment.title')}>
        <Icon name="wallet" size={20}/><span><b>{t('inbox.uploadPaymentPrivate')}</b>{bankMissing && <small>{t('inbox.uploadPaymentPrivateRecommended')}</small>}</span>
      </button>
    </div>
    <button type="button" className="camera-payment-choice is-analyze" disabled={disabled}
      onClick={() => onPick({ kind: 'analyze' })} data-help={t('helpTopics.aiProposal.body')} data-help-title={t('helpTopics.aiProposal.title')}>
      <Icon name="sparkle" size={20}/><span  ><b>{t('inbox.uploadPaymentAnalyzeShort')}</b></span>
    </button>
    {children}
    {/* Nedtrekket er utveien, ikke hovedveien: det staar under knappene og sier
        hva det er til. Et valg her er like endelig som en knapp over. */}
    <InvoicePayAccountSelect value="" onChange={pickAccount} disabled={disabled}
      emptyLabel={t('inbox.uploadPaymentOtherSource')}/>
    {bankSetupOpen && <AddBankAccountDialog
      bankOnly
      onClose={() => setBankSetupOpen(false)}
      onAdded={(accountCode) => { setBankSetupOpen(false); pickAccount(accountCode); }}
    />}
  </div>;
}

function ReceiptSinglePaymentSourceDialog({ open = true, onClose, onConfirm, busy = false }) {
  return <Modal open={open} portal top onClose={busy ? () => {} : onClose}
    title={t('inbox.uploadPaymentSingleTitle')} max={560}
    footer={<Button variant="ghost" disabled={busy} onClick={onClose}>{t('common.cancel')}</Button>}>
    <ReceiptPaymentSourceChoices disabled={busy}
      onPick={(decision) => onConfirm({ ...decision, origin: 'manual-at-upload' })}/>
  </Modal>;
}

function ReceiptUploadPaymentDialog({ files, onClose, onConfirm, busy = false }) {
  const rows = React.useMemo(() => Array.from(files || []).map((file, index) => ({
    id: receiptUploadFileKey(file, index),
    file,
  })), [files]);
  const single = rows.length === 1;
  const [decisions, setDecisions] = React.useState({});
  const [selected, setSelected] = React.useState(() => new Set());
  const anchorRef = React.useRef(null);
  const [pendingOnly, setPendingOnly] = React.useState(false);
  const completed = rows.filter((row) => decisions[row.id]).length;
  const visibleRows = pendingOnly ? rows.filter((row) => !decisions[row.id]) : rows;
  const selectedVisible = visibleRows.filter((row) => selected.has(row.id));
  const selectedIds = [...selected];
  const allComplete = rows.length > 0 && completed === rows.length;

  // Bare bulkdelen samler opp beslutninger; ett bilag lastes opp i samme trykk
  // som valget.
  const setForIds = (ids, decision) => {
    if (!ids.length) return;
    setDecisions((current) => {
      const next = { ...current };
      ids.forEach((id) => { next[id] = { ...decision, origin: 'manual-at-upload' }; });
      return next;
    });
    anchorRef.current = null;
    setSelected(new Set());
  };
  const assignKind = (kind) => setForIds(selectedIds, { kind });
  const toggleRow = (id, checked) => {
    anchorRef.current = id;
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };
  const pickModifiedRow = (id, event) => {
    const selection = window.PlummeListSelection;
    const extend = selection.isExtendClick(event);
    if (!extend && !selection.isPickClick(event)) return;
    event.preventDefault();
    event.stopPropagation();
    if (extend) selection.clearTextSelection();
    const next = selection.nextSelection({
      ids: visibleRows.map((row) => row.id),
      selected: [...selected],
      anchorId: anchorRef.current,
      targetId: id,
      extend,
    });
    anchorRef.current = next.anchorId;
    setSelected(new Set(next.selected));
  };
  const selectVisible = (checked) => {
    anchorRef.current = null;
    setSelected((current) => {
      const next = new Set(current);
      visibleRows.forEach((row) => { if (checked) next.add(row.id); else next.delete(row.id); });
      return next;
    });
  };
  const confirm = () => onConfirm(rows.map((row) => ({
    file: row.file,
    paymentSourceDecision: decisions[row.id],
  })));
  // Ett bilag har ingenting aa samle opp: valget er hele handlingen, og
  // opplastingen skjer i samme trykk. Beslutningen bygges her i stedet for aa gaa
  // via `decisions`, som ikke er skrevet enda i det denne kjorer.
  const confirmSingle = (decision) => onConfirm([{
    file: rows[0].file,
    paymentSourceDecision: { ...decision, origin: 'manual-at-upload' },
  }]);

  // Med ett bilag er Avbryt det eneste som staar igjen i foten.
  const footer = single
    ? <Button variant="ghost" disabled={busy} onClick={onClose}>{t('common.cancel')}</Button>
    : <React.Fragment>
      <Button variant="ghost" disabled={busy} onClick={onClose}>{t('common.cancel')}</Button>
      <Button icon="upload" disabled={busy || !allComplete} onClick={confirm}>
        {busy ? t('common.working') : t('inbox.uploadPaymentConfirm', { count: rows.length })}
      </Button>
    </React.Fragment>;

  return <Modal open portal onClose={busy ? () => {} : onClose}
    title={single ? t('inbox.uploadPaymentSingleTitle') : t('inbox.uploadPaymentBulkTitle')}
    max={single ? 560 : 820} footer={footer}>
    <div className="receipt-upload-payment">
      {single ? <React.Fragment>
        {/* Fila staar overst: valget under gjelder akkurat dette bilaget. */}
        <div className="receipt-upload-file single">
          <Icon name={rows[0].file?.type === 'application/pdf' ? 'doc' : 'receipt'} size={15}/>
          <span>{rows[0].file?.name || t('inbox.uploadPaymentReceipt')}</span>
        </div>
        <ReceiptPaymentSourceChoices disabled={busy} onPick={confirmSingle}>
          {/* Den ene muligheten kameraet ikke har: leverandorfakturaen som skal
              staa som gjeld til den betales. Egen linje, fordi den ikke er en
              betalingskilde — den sier at det ikke finnes noen enda. */}
          <button type="button" className="camera-payment-choice is-detail" disabled={busy}
            onClick={() => confirmSingle({ kind: 'unpaid' })} data-help={t('helpTopics.uploadPayment.body')} data-help-title={t('helpTopics.uploadPayment.title')}>
            <Icon name="calendar" size={20}/>
            <span  ><b>{t('inbox.uploadPaymentUnpaid')}</b><small>{t('inbox.uploadPaymentUnpaidHint')}</small></span>
          </button>
        </ReceiptPaymentSourceChoices>
      </React.Fragment> : <React.Fragment>
        <div className="receipt-upload-progress">
          <b>{t('inbox.uploadPaymentProgress', { completed, count: rows.length })}</b>
          <div><span style={{ width: `${rows.length ? (completed / rows.length) * 100 : 0}%` }}/></div>
        </div>
        {/* Samme knapperad som for ett bilag. Forskjellen er hva et trykk gjor:
            her settes kilden paa radene brukeren har merket, i stedet for aa
            laste opp med det samme. Linja over sier hvor mange det gjelder —
            uten den ville en sperret knapperad ikke forklart seg selv. */}
        <div className="receipt-upload-assign">
          <span className="receipt-upload-assign-scope">
            {selectedIds.length
              ? t('inbox.uploadPaymentAppliesTo', { count: selectedIds.length })
              : t('inbox.uploadPaymentSelectFirst')}
          </span>
          <ReceiptPaymentSourceChoices disabled={busy || !selectedIds.length}
            onPick={(decision) => setForIds(selectedIds, decision)}>
            <button type="button" className="camera-payment-choice is-detail" disabled={busy || !selectedIds.length}
              onClick={() => assignKind('unpaid')} data-help={t('helpTopics.uploadPayment.body')} data-help-title={t('helpTopics.uploadPayment.title')}>
              <Icon name="calendar" size={20}/>
              <span  ><b>{t('inbox.uploadPaymentUnpaid')}</b><small>{t('inbox.uploadPaymentUnpaidHint')}</small></span>
            </button>
          </ReceiptPaymentSourceChoices>
        </div>
        <div className="receipt-upload-list-head">
          <Checkbox checked={visibleRows.length > 0 && selectedVisible.length === visibleRows.length} onChange={selectVisible}>{t('inbox.uploadPaymentSelectVisible')}</Checkbox>
          <button type="button" onClick={() => setPendingOnly((current) => !current)}>{pendingOnly ? t('inbox.uploadPaymentShowAll') : t('inbox.uploadPaymentShowPending')}</button>
        </div>
        <div className="receipt-upload-list">
          {visibleRows.map((row) => {
            const decision = decisions[row.id];
            return <div key={row.id} className={'receipt-upload-file' + (decision ? ' complete' : '')}
              onClickCapture={(event) => pickModifiedRow(row.id, event)}>
              <Checkbox checked={selected.has(row.id)} onChange={(checked) => toggleRow(row.id, checked)}>
                <span className="receipt-upload-file-name"><Icon name={row.file?.type === 'application/pdf' ? 'doc' : 'receipt'} size={18}/>{row.file?.name || t('inbox.uploadPaymentReceipt')}</span>
              </Checkbox>
              <Badge variant={decision ? 'success' : 'neutral'}>{receiptUploadDecisionLabel(decision)}</Badge>
            </div>;
          })}
        </div>
      </React.Fragment>}
    </div>
  </Modal>;
}

Object.assign(window, { ReceiptSinglePaymentSourceDialog, ReceiptUploadPaymentDialog, receiptUploadDecisionLabel });
